import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  Calendar,
  MessageCircle,
  Star,
  Shield,
  ArrowRight,
  MapPin,
  Users,
  Clock,
} from 'lucide-react';
import { SERVICE_CATALOG } from '../config/services';
import ScrollReveal from '../components/ui/ScrollReveal';
import ScrollRevealStack from '../components/ui/ScrollRevealStack';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(selectedService ? `/search?service=${selectedService}` : '/search');
  };

  const whyBenefits = [
    'Browse caregivers by service, location, and rating',
    'Message before you book. Ask about diet, meds, and routines',
    'Track bookings from request to completion',
    'Leave reviews that help the next pet parent decide',
  ];

  const platformStats = [
    { label: 'Services', value: '8+', sub: 'Care types' },
    { label: 'Cities', value: '3+', sub: 'And growing' },
    { label: 'Booking', value: '24/7', sub: 'Self-serve' },
    { label: 'Support', value: 'Live', sub: 'When you need it' },
  ];

  const tickerServices = [...SERVICE_CATALOG, ...SERVICE_CATALOG];

  const steps = [
    { icon: Search, title: 'Search locally', desc: 'Browse verified caregivers near you' },
    { icon: MessageCircle, title: 'Talk first', desc: 'Chat before you commit to anything' },
    { icon: Calendar, title: 'Book with confidence', desc: 'Schedule and track every stay' },
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      location: 'Hyderabad',
      text: 'Finding someone in my neighbourhood was straightforward. I could read reviews and message caregivers before booking.',
      pet: 'Golden Retriever',
    },
    {
      name: 'Raj K.',
      location: 'Bangalore',
      text: 'The booking flow kept everything in one place: dates, messages, and payment details. Much easier than coordinating over WhatsApp.',
      pet: 'Persian Cat',
    },
    {
      name: 'Priya S.',
      location: 'Mumbai',
      text: 'I travel often for work. Having a platform to manage my beagle\'s care gives me real peace of mind.',
      pet: 'Beagle',
    },
  ];

  return (
    <div className="pf-page-wrap pb-0">
      {/* Hero */}
      <section className="pf-hero-v2 relative">
        <div className="pf-hero-v2__pattern" />
        <div className="pf-hero-v2__glow" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12 relative z-10">
          <div className="pf-hero-layout">
            <div className="pf-hero-copy">
              <h1 className="pf-display text-4xl sm:text-5xl lg:text-[3.25rem] text-white mb-4">
                Care for your pet,
                <span className="block text-blue-200">
                  close to home
                </span>
              </h1>
              <p className="text-base sm:text-lg text-blue-100/90 max-w-lg leading-relaxed">
                Connect with independent caregivers in your city. Search, message, and book, all through one trusted platform.
              </p>
            </div>

            <div className="pf-hero-actions">
              <form onSubmit={handleSearch} className="pf-search-bento pf-search-bento--hero pf-glass pf-glass--hero w-full">
                <label htmlFor="hero-service" className="sr-only">Select a service</label>
                <div className="pf-hero-search-row flex flex-col md:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--pf-ink-muted)]" />
                    <select
                      id="hero-service"
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className="pf-input w-full pl-10"
                    >
                      <option value="">All services</option>
                      {SERVICE_CATALOG.map((s) => (
                        <option key={s.slug} value={s.slug}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="pf-btn-primary shrink-0">
                    Find caregivers
                  </button>
                </div>
              </form>

              <div className="pf-hero-actions__pills">
                <span className="pf-stat-pill pf-glass-pill text-white">
                  <Users className="w-3.5 h-3.5" /> Local caregivers
                </span>
                <span className="pf-stat-pill pf-glass-pill text-white">
                  <Clock className="w-3.5 h-3.5" /> Same-day booking
                </span>
                <span className="pf-stat-pill pf-glass-pill text-white">
                  <MapPin className="w-3.5 h-3.5" /> Hyderabad & beyond
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="pf-hero-ticker relative z-10" aria-label="Service starting prices">
          <div className="pf-hero-ticker__fade pf-hero-ticker__fade--left" aria-hidden="true" />
          <div className="pf-hero-ticker__fade pf-hero-ticker__fade--right" aria-hidden="true" />
          <div className="pf-hero-ticker__viewport">
            <div className="pf-hero-ticker__track">
              {tickerServices.map((service, index) => {
                const ServiceIcon = service.icon;
                return (
                  <Link
                    key={`${service.slug}-${index}`}
                    to={`/search?service=${service.slug}`}
                    className="pf-hero-ticker__item"
                  >
                    <ServiceIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="pf-hero-ticker__label">{service.label}</span>
                    <span className="pf-hero-ticker__price">{service.price}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-10 lg:py-14 bg-[var(--pf-canvas)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pf-home-pods">
          <ScrollReveal direction="left" className="mb-8">
            <div className="pf-pod-card pf-pod-card--header">
              <span className="pf-eyebrow mb-2">How it works</span>
              <h2 className="pf-display text-3xl sm:text-4xl mt-2 mb-2">Three steps to trusted care</h2>
              <p className="text-[var(--pf-ink-muted)] max-w-2xl">
                No complicated onboarding. Find someone nearby, talk through your pet&apos;s needs, then book.
              </p>
            </div>
          </ScrollReveal>

          <div className="pf-home-pods__stack">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <ScrollReveal
                  key={step.title}
                  direction={index % 2 === 0 ? 'left' : 'right'}
                  delayMs={index * 135}
                >
                  <article className="pf-pod-card pf-pod-card--step">
                    <div className="pf-pod-card__step-num">{String(index + 1).padStart(2, '0')}</div>
                    <div className="pf-pod-card__icon">
                      <StepIcon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div className="pf-pod-card__body">
                      <h3 className="font-semibold text-[var(--pf-ink)] text-lg mb-1">{step.title}</h3>
                      <p className="text-sm text-[var(--pf-ink-muted)] leading-relaxed">{step.desc}</p>
                    </div>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>

          {!user && (
            <ScrollReveal direction="right" className="mt-8 text-center">
              <Link to="/signup" className="pf-btn-primary inline-flex items-center gap-2">
                Create free account
                <ArrowRight className="w-4 h-4" />
              </Link>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* Why PetsFostering */}
      <section className="py-10 lg:py-14 bg-[var(--pf-surface)] border-y border-[var(--pf-border)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pf-home-pods">
          <ScrollReveal direction="left" className="mb-6">
            <div className="pf-pod-card pf-pod-card--header">
              <span className="pf-eyebrow mb-2">Why PetsFostering</span>
              <h2 className="pf-display text-3xl sm:text-4xl mt-2 mb-2">
                Built for real pet parents, not generic listings
              </h2>
              <p className="text-[var(--pf-ink-muted)] leading-relaxed">
                Every caregiver on the platform has a profile you can review. Every booking lives in your dashboard with messages, dates, and history together.
              </p>
            </div>
          </ScrollReveal>

          <div className="pf-home-pods__stack">
            <ScrollReveal direction="left">
              <article className="pf-pod-card pf-pod-card--benefits">
                <ul className="pf-pod-benefits">
                  {whyBenefits.map((item) => (
                    <li key={item} className="pf-pod-benefits__item">
                      <Star className="w-4 h-4 text-[var(--pf-accent)] shrink-0 fill-current" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <article className="pf-pod-card pf-pod-card--stats">
                <div className="pf-pod-stats">
                  {platformStats.map((stat) => (
                    <div key={stat.label} className="pf-pod-stats__cell">
                      <p className="pf-display text-2xl text-[var(--pf-accent)]">{stat.value}</p>
                      <p className="text-sm font-semibold text-[var(--pf-ink)]">{stat.label}</p>
                      <p className="text-xs text-[var(--pf-ink-muted)]">{stat.sub}</p>
                    </div>
                  ))}
                </div>
              </article>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left" className="text-center mb-8">
            <span className="pf-eyebrow mb-3">Community</span>
            <h2 className="pf-display text-3xl sm:text-4xl mt-2">What pet parents say</h2>
          </ScrollReveal>

          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-3 gap-5" staggerMs={180}>
            {testimonials.map((t) => (
              <article key={t.name} className="pf-organic-card p-5 flex flex-col h-full">
                <span className="pf-quote-mark leading-none">&ldquo;</span>
                <p className="text-[var(--pf-ink-muted)] leading-relaxed flex-1 -mt-4 mb-4">{t.text}</p>
                <div className="flex items-center gap-3 pt-3 border-t border-[var(--pf-border)]">
                  <div className="w-10 h-10 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white font-bold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--pf-ink)] text-sm">{t.name}</p>
                    <p className="text-xs text-[var(--pf-ink-muted)]">{t.pet} · {t.location}</p>
                  </div>
                </div>
              </article>
            ))}
          </ScrollRevealStack>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 border-t border-[var(--pf-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="right">
            <div className="pf-organic-card pf-organic-card--flat p-5 sm:p-6 flex gap-4">
              <Shield className="w-6 h-6 text-[var(--pf-accent)] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-[var(--pf-ink)] mb-2">Platform disclaimer</h3>
                <p className="text-sm text-[var(--pf-ink-muted)] leading-relaxed">
                  PetsFostering connects pet owners with independent caregivers. We facilitate bookings and communication, but do not employ caregivers or provide pet care directly. Users are responsible for verifying credentials and agreeing on terms.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pf-cta-band py-10 relative overflow-hidden">
        <div className="pf-hero-v2__pattern opacity-20 absolute inset-0" />
        <ScrollReveal direction="left" className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <h2 className="pf-display text-3xl sm:text-4xl text-white mb-3">
            Ready to find care nearby?
          </h2>
          <p className="text-white/85 mb-6 text-base sm:text-lg">
            Join PetsFostering to discover local caregivers and manage bookings in one place.
          </p>
          {!user && (
            <Link to="/signup" className="pf-btn-community inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold">
              Get started free
              <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Home;
